import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import {
  applyMuslinInventoryImport,
  applyMuslinDesignBackfill,
  bulkAssignMuslinInventoryStore,
  bulkDeleteMuslinInventoryItems,
  createMuslinInventoryItem,
  deleteMuslinInventoryItem,
  getMuslinInventory,
  previewMuslinInventoryImport,
  previewMuslinDesignBackfill,
  releaseMuslinInventoryItem,
  updateMuslinInventoryItem,
} from '@/lib/api';
import { MuslinInventoryFormDialog } from '@/components/muslin/MuslinInventoryFormDialog';
import { MuslinDesignBackfillDialog } from '@/components/muslin/MuslinDesignBackfillDialog';
import { ArchiveRestore, ExternalLink, Package2, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react';

const statusTone = {
  available: 'bg-emerald-500/10 text-emerald-700 border-emerald-300',
  reserved: 'bg-amber-500/10 text-amber-700 border-amber-300',
  retired: 'bg-slate-500/10 text-slate-700 border-slate-300',
};

const measurementTone = {
  complete: 'bg-emerald-500/10 text-emerald-700 border-emerald-300',
  partial: 'bg-amber-500/10 text-amber-700 border-amber-300',
  missing: 'bg-rose-500/10 text-rose-700 border-rose-300',
  no_pattern: 'bg-slate-500/10 text-slate-700 border-slate-300',
};

const measurementLabel = {
  complete: 'Snapshot ready',
  partial: 'Snapshot incomplete',
  missing: 'Snapshot missing',
  no_pattern: 'No snapshot',
};

const normalizeBoxDisplay = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  return raw
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((token) => token
      .split('-')
      .map((segment) => {
        if (!segment) return segment;
        return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
      })
      .join('-'))
    .join(' ');
};

export default function MuslinInventoryPage() {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [boxFilter, setBoxFilter] = useState('all');
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetTabName, setSheetTabName] = useState('');
  const [importStore, setImportStore] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyingImport, setApplyingImport] = useState(false);
  const [designBackfillDialogOpen, setDesignBackfillDialogOpen] = useState(false);
  const [designBackfillLoading, setDesignBackfillLoading] = useState(false);
  const [designBackfillApplying, setDesignBackfillApplying] = useState(false);
  const [designBackfillPreview, setDesignBackfillPreview] = useState(null);
  const [designBackfillScopeIds, setDesignBackfillScopeIds] = useState([]);
  const [designBackfillScopeLabel, setDesignBackfillScopeLabel] = useState('visible rows');
  const [bulkStore, setBulkStore] = useState('');
  const [bulkUpdatingStore, setBulkUpdatingStore] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const requestCounterRef = useRef(0);

  const isDeleteBlocked = (item) => Boolean(
    item?.status === 'reserved'
    || item?.reserved_for_order_id
    || item?.reserved_for_order_number
    || item?.reserved_for_garment_id
    || item?.reserved_for_garment_no
    || item?.reserved_for_test_fit_date
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const fetchItems = async (overrideFilters = {}, options = {}) => {
    const keepVisibleData = Boolean(options.keepVisibleData);
    const requestId = requestCounterRef.current + 1;
    requestCounterRef.current = requestId;

    if (keepVisibleData) {
      setIsFetching(true);
    } else {
      setLoading(true);
    }

    try {
      const params = {
        search: (overrideFilters.search ?? debouncedSearch) || undefined,
        status: (overrideFilters.statusFilter ?? statusFilter) !== 'all' ? (overrideFilters.statusFilter ?? statusFilter) : undefined,
        store: (overrideFilters.storeFilter ?? storeFilter) !== 'all' ? (overrideFilters.storeFilter ?? storeFilter) : undefined,
        garment_type: (overrideFilters.typeFilter ?? typeFilter) !== 'all' ? (overrideFilters.typeFilter ?? typeFilter) : undefined,
      };
      const [itemsRes, storesRes] = await Promise.all([
        getMuslinInventory(params),
        getCachedAccessibleStores(),
      ]);
      if (requestCounterRef.current !== requestId) {
        return;
      }
      setItems(itemsRes.data || []);
      setStores((storesRes || []).map((store) => store.name || store.store_id).filter(Boolean));
    } catch (error) {
      if (requestCounterRef.current !== requestId) {
        return;
      }
      console.error('Failed to fetch muslin inventory:', error);
      toast.error('Failed to load muslin inventory');
    } finally {
      if (requestCounterRef.current !== requestId) {
        return;
      }
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchItems({}, { keepVisibleData: items.length > 0 });
  }, [debouncedSearch, statusFilter, storeFilter, typeFilter]);

  const handleResetImport = () => {
    setSheetUrl('');
    setSheetTabName('');
    setImportStore('');
    setImportPreview(null);
    setSelectedCells([]);
  };

  const handlePreviewImport = async () => {
    if (!sheetUrl) {
      toast.error('Paste the Google Sheet URL first');
      return;
    }
    if (!importStore) {
      toast.error('Select a store for this import first');
      return;
    }
    setPreviewLoading(true);
    try {
      const response = await previewMuslinInventoryImport({ sheet_url: sheetUrl, tab_name: sheetTabName || null });
      setImportPreview(response.data);
      setSelectedCells((response.data.items || []).map((item) => item.source_cell));
      toast.success(`Found ${response.data.total_items || 0} muslin entries`);
    } catch (error) {
      console.error('Failed to preview import:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview Google Sheet import');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApplyImport = async () => {
    if (!importPreview) return;
    if (!importStore) {
      toast.error('Select a store before importing muslins');
      return;
    }
    setApplyingImport(true);
    try {
      const response = await applyMuslinInventoryImport({
        sheet_url: sheetUrl,
        tab_name: importPreview.selected_tab_name || sheetTabName || null,
        selected_cells: selectedCells,
        store: importStore,
      });
      toast.success(
        `Imported ${response.data.imported || 0} new, updated ${response.data.updated || 0} boxes (${response.data.skipped || 0} unchanged)`
      );
      setSearch('');
      setStatusFilter('all');
      setStoreFilter('all');
      setTypeFilter('all');
      setBoxFilter('all');
      handleResetImport();
      setImportDialogOpen(false);
      await fetchItems({ search: '', statusFilter: 'all', storeFilter: 'all', typeFilter: 'all' }, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to apply import:', error);
      toast.error(error.response?.data?.detail || 'Failed to import muslin inventory');
    } finally {
      setApplyingImport(false);
    }
  };

  const handleSubmitItem = async (payload) => {
    setDialogSaving(true);
    try {
      if (editingItem?.muslin_inventory_id) {
        await updateMuslinInventoryItem(editingItem.muslin_inventory_id, payload);
        toast.success('Muslin inventory updated');
      } else {
        await createMuslinInventoryItem(payload);
        toast.success('Muslin inventory item created');
      }
      setDialogOpen(false);
      setEditingItem(null);
      await fetchItems({}, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to save muslin inventory item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save muslin inventory item');
    } finally {
      setDialogSaving(false);
    }
  };

  const handleRelease = async (item) => {
    try {
      await releaseMuslinInventoryItem(item.muslin_inventory_id, { garment_id: item.reserved_for_garment_id || null });
      toast.success(`Released ${item.reference_code}`);
      await fetchItems({}, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to release muslin item:', error);
      toast.error('Failed to release muslin item');
    }
  };

  const handleSelectRow = (itemId, checked) => {
    setSelectedItemIds((prev) => {
      if (checked) {
        return prev.includes(itemId) ? prev : [...prev, itemId];
      }
      return prev.filter((currentId) => currentId !== itemId);
    });
  };

  const handleSelectAllRows = (checked) => {
    if (checked) {
      setSelectedItemIds(displayedItems.map((item) => item.muslin_inventory_id));
      return;
    }
    setSelectedItemIds([]);
  };

  const handleBulkAssignStore = async () => {
    if (selectedItemIds.length === 0) {
      toast.error('Select one or more muslin records first');
      return;
    }
    if (!bulkStore) {
      toast.error('Choose a store to assign');
      return;
    }

    setBulkUpdatingStore(true);
    try {
      const response = await bulkAssignMuslinInventoryStore(selectedItemIds, bulkStore);
      const payload = response.data || {};
      if (payload.updated_count) {
        toast.success(`Assigned ${payload.store} to ${payload.updated_count} muslin record${payload.updated_count === 1 ? '' : 's'}`);
      }
      if (payload.unchanged_count) {
        toast.info(`${payload.unchanged_count} selected record${payload.unchanged_count === 1 ? ' already has' : 's already have'} ${payload.store}`);
      }
      if (payload.missing_ids?.length) {
        toast.error(`${payload.missing_ids.length} selected record${payload.missing_ids.length === 1 ? ' was' : 's were'} unavailable during the update`);
      }
      setSelectedItemIds([]);
      await fetchItems({}, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to bulk assign muslin inventory store:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign store to selected muslin records');
    } finally {
      setBulkUpdatingStore(false);
    }
  };

  const loadDesignBackfillPreview = async (muslinIds, scopeLabel) => {
    if (!muslinIds.length) {
      toast.error('No muslin records are available in this scope');
      return;
    }
    setDesignBackfillScopeIds(muslinIds);
    setDesignBackfillScopeLabel(scopeLabel);
    setDesignBackfillLoading(true);
    setDesignBackfillDialogOpen(true);
    try {
      const response = await previewMuslinDesignBackfill(muslinIds);
      setDesignBackfillPreview(response.data || null);
    } catch (error) {
      console.error('Failed to preview muslin design backfill:', error);
      toast.error(error.response?.data?.detail || 'Failed to review historical muslin design details');
      setDesignBackfillDialogOpen(false);
    } finally {
      setDesignBackfillLoading(false);
    }
  };

  const handleOpenDesignBackfillDialog = async () => {
    const scopeIds = selectedItemIds.length > 0
      ? selectedItemIds
      : displayedItems.map((item) => item.muslin_inventory_id);
    const scopeLabel = selectedItemIds.length > 0
      ? `${selectedItemIds.length} selected muslin row${selectedItemIds.length === 1 ? '' : 's'}`
      : `${displayedItems.length} visible muslin row${displayedItems.length === 1 ? '' : 's'}`;
    await loadDesignBackfillPreview(scopeIds, scopeLabel);
  };

  const handleApplyDesignBackfill = async () => {
    if (!designBackfillScopeIds.length) {
      toast.error('No muslin records are available for backfill');
      return;
    }
    setDesignBackfillApplying(true);
    try {
      const response = await applyMuslinDesignBackfill(designBackfillScopeIds);
      const payload = response.data || {};
      if (payload.applied_count) {
        toast.success(`Backfilled design details for ${payload.applied_count} muslin record${payload.applied_count === 1 ? '' : 's'}`);
      } else {
        toast.info('No safe muslin design suggestions were applied');
      }
      await fetchItems({}, { keepVisibleData: true });
      await loadDesignBackfillPreview(designBackfillScopeIds, designBackfillScopeLabel);
    } catch (error) {
      console.error('Failed to apply muslin design backfill:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply muslin design backfill');
    } finally {
      setDesignBackfillApplying(false);
    }
  };

  const handleDeleteSingle = async () => {
    if (!deleteTarget?.muslin_inventory_id) return;
    setDeleting(true);
    try {
      await deleteMuslinInventoryItem(deleteTarget.muslin_inventory_id);
      toast.success(`${deleteTarget.reference_code} deleted`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchItems({}, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to delete muslin inventory item:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete muslin inventory item');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItemIds.length === 0) {
      toast.error('Select one or more unreserved muslin records first');
      return;
    }
    setDeleting(true);
    try {
      const response = await bulkDeleteMuslinInventoryItems(selectedItemIds);
      const payload = response.data || {};
      const blockedItems = payload.blocked_items || [];
      if (payload.deleted_count) {
        toast.success(`Deleted ${payload.deleted_count} muslin record${payload.deleted_count === 1 ? '' : 's'}`);
      }
      if (blockedItems.length > 0) {
        toast.error(`${blockedItems.length} selected record${blockedItems.length === 1 ? ' is' : 's are'} reserved or linked. Release them first.`);
      }
      setBulkDeleteDialogOpen(false);
      setSelectedItemIds([]);
      await fetchItems({}, { keepVisibleData: true });
    } catch (error) {
      console.error('Failed to bulk delete muslin inventory items:', error);
      toast.error(error.response?.data?.detail || 'Failed to bulk delete muslin inventory items');
    } finally {
      setDeleting(false);
    }
  };

  const coverageCounts = useMemo(() => ({
    all: items.length,
    complete: items.filter((item) => item.measurement_coverage_status === 'complete').length,
    needs_measurements: items.filter((item) => ['partial', 'missing'].includes(item.measurement_coverage_status)).length,
    no_pattern: items.filter((item) => item.measurement_coverage_status === 'no_pattern').length,
  }), [items]);

  const boxOptions = useMemo(
    () => Array.from(new Set(items.map((item) => normalizeBoxDisplay(item.box_code)).filter((value) => value && value !== '—'))).sort(),
    [items],
  );

  const displayedItems = useMemo(() => {
    const coverageFilteredItems = (() => {
    if (coverageFilter === 'complete') {
      return items.filter((item) => item.measurement_coverage_status === 'complete');
    }
    if (coverageFilter === 'needs_measurements') {
      return items.filter((item) => ['partial', 'missing'].includes(item.measurement_coverage_status));
    }
    if (coverageFilter === 'no_pattern') {
      return items.filter((item) => item.measurement_coverage_status === 'no_pattern');
    }
    return items;
    })();

    if (boxFilter !== 'all') {
      return coverageFilteredItems.filter((item) => normalizeBoxDisplay(item.box_code) === boxFilter);
    }

    return coverageFilteredItems;
  }, [boxFilter, coverageFilter, items]);

  useEffect(() => {
    setSelectedItemIds((prev) => prev.filter((itemId) => displayedItems.some((item) => item.muslin_inventory_id === itemId)));
  }, [displayedItems]);

  const allVisibleSelected = displayedItems.length > 0 && displayedItems.every((item) => selectedItemIds.includes(item.muslin_inventory_id));
  const selectedVisibleCount = selectedItemIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const showSnapshotDetails = coverageFilter === 'needs_measurements' || coverageFilter === 'no_pattern';
  const emptyStateMessage = coverageFilter === 'needs_measurements'
    ? 'No muslin items are waiting on snapshot completion.'
    : coverageFilter === 'no_pattern'
      ? 'No muslin items are missing reference patterns in the app.'
      : coverageFilter === 'complete'
        ? 'No muslin items with complete snapshot data match the current filters.'
        : 'No muslin inventory items match the current filters.';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.14),_transparent_28%),linear-gradient(180deg,#fffdf7_0%,#f8fafc_55%,#eef2ff_100%)]">
      <div className="mx-auto flex w-full max-w-[1760px] flex-col gap-3 px-3 py-3 lg:px-4" data-testid="muslin-inventory-page">
        <div className="sticky top-0 z-30 bg-[linear-gradient(180deg,rgba(255,253,247,0.98)_0%,rgba(248,250,252,0.94)_70%,rgba(248,250,252,0)_100%)] pb-2 pt-0 backdrop-blur-sm">
        <section className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-white/92 px-4 py-3 shadow-sm" data-testid="muslin-inventory-hero-card">
          <CardTitle className="text-xl font-semibold tracking-tight text-slate-900">Muslin Inventory</CardTitle>
          <div className="flex items-start justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              className="h-11 border-amber-200 bg-white/92 px-4 text-slate-900 shadow-sm hover:bg-amber-50"
              data-testid="muslin-import-open-button"
            >
              <Sparkles className="mr-2 h-4 w-4 text-amber-600" /> Import from Google Sheet
            </Button>
          </div>
        </section>
        </div>

        <Card className="border-slate-200 bg-white/95 shadow-sm" data-testid="muslin-inventory-table-card">
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <Tabs value={coverageFilter} onValueChange={setCoverageFilter} data-testid="muslin-inventory-coverage-tabs">
                <TabsList className="h-auto flex-wrap justify-start rounded-2xl bg-amber-50 p-1.5">
                  <TabsTrigger value="all" data-testid="muslin-inventory-coverage-tab-all">All ({coverageCounts.all})</TabsTrigger>
                  <TabsTrigger value="complete" data-testid="muslin-inventory-coverage-tab-complete">Ready ({coverageCounts.complete})</TabsTrigger>
                  <TabsTrigger value="needs_measurements" data-testid="muslin-inventory-coverage-tab-needs-measurements">Needs Review ({coverageCounts.needs_measurements})</TabsTrigger>
                  <TabsTrigger value="no_pattern" data-testid="muslin-inventory-coverage-tab-no-pattern">No Pattern ({coverageCounts.no_pattern})</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search code, pattern, box, garment"
                className="w-full md:w-[220px]"
                data-testid="muslin-inventory-search-input"
              />
              {(search !== debouncedSearch || isFetching) && (
                <Badge
                  variant="outline"
                  className="border-amber-200 bg-amber-50 text-slate-700"
                  data-testid="muslin-inventory-refreshing-badge"
                >
                  {search !== debouncedSearch ? 'Waiting for search…' : 'Refreshing rows…'}
                </Badge>
              )}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="muslin-inventory-status-filter-trigger"><SelectValue /></SelectTrigger>
                <SelectContent data-testid="muslin-inventory-status-filter-content">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="w-[150px]" data-testid="muslin-inventory-store-filter-trigger"><SelectValue /></SelectTrigger>
                <SelectContent data-testid="muslin-inventory-store-filter-content">
                  <SelectItem value="all">All stores</SelectItem>
                  {stores.map((store) => <SelectItem key={store} value={store}>{store}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={boxFilter} onValueChange={setBoxFilter}>
                <SelectTrigger className="w-[150px]" data-testid="muslin-inventory-box-filter-trigger"><SelectValue /></SelectTrigger>
                <SelectContent data-testid="muslin-inventory-box-filter-content">
                  <SelectItem value="all">All boxes</SelectItem>
                  {boxOptions.map((box) => <SelectItem key={box} value={box}>{box}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]" data-testid="muslin-inventory-type-filter-trigger"><SelectValue /></SelectTrigger>
                <SelectContent data-testid="muslin-inventory-type-filter-content">
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="jacket">Jacket</SelectItem>
                  <SelectItem value="trouser">Trouser</SelectItem>
                  <SelectItem value="shirt">Shirt</SelectItem>
                  <SelectItem value="waistcoat">Waistcoat</SelectItem>
                  <SelectItem value="overcoat">Overcoat</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => fetchItems({}, { keepVisibleData: displayedItems.length > 0 })}
                disabled={isFetching}
                data-testid="muslin-inventory-refresh-button"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                {isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Button variant="outline" onClick={handleOpenDesignBackfillDialog} data-testid="muslin-design-backfill-open-button"><Sparkles className="mr-2 h-4 w-4" /> Review Design Backfill</Button>
              <Button onClick={() => { setEditingItem(null); setDialogOpen(true); }} data-testid="muslin-inventory-add-button"><Plus className="mr-2 h-4 w-4" /> Add Item</Button>
              {selectedVisibleCount > 0 && (
                <Badge className="border border-amber-200 bg-amber-50 text-slate-700" data-testid="muslin-inventory-selected-inline-badge">
                  {selectedVisibleCount} selected
                </Badge>
              )}
            </div>

            {selectedVisibleCount > 0 && (
              <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50/80 p-2.5 lg:flex-row lg:items-center lg:justify-between" data-testid="muslin-inventory-bulk-actions-bar">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium text-slate-900" data-testid="muslin-inventory-selected-count-text">
                    {selectedVisibleCount} selected of {displayedItems.length} visible row{displayedItems.length === 1 ? '' : 's'}
                  </div>
                  <div className="text-[11px] text-slate-500" data-testid="muslin-inventory-selection-help-text">
                    Select all applies to the current visible and filtered rows.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={bulkStore} onValueChange={setBulkStore}>
                    <SelectTrigger className="w-[210px]" data-testid="muslin-inventory-bulk-store-trigger"><SelectValue placeholder="Assign selected to store" /></SelectTrigger>
                    <SelectContent data-testid="muslin-inventory-bulk-store-content">
                      {stores.map((store) => <SelectItem key={store} value={store}>{store}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleBulkAssignStore}
                    disabled={bulkUpdatingStore || !bulkStore || selectedVisibleCount === 0}
                    data-testid="muslin-inventory-bulk-store-assign-button"
                  >
                    {bulkUpdatingStore ? 'Assigning...' : `Assign Store (${selectedVisibleCount})`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedItemIds([])}
                    disabled={selectedVisibleCount === 0}
                    data-testid="muslin-inventory-clear-selection-button"
                  >
                    Clear Selection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    disabled={selectedVisibleCount === 0}
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    data-testid="muslin-inventory-bulk-delete-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete Selected ({selectedVisibleCount})
                  </Button>
                </div>
              </div>
            )}

            {loading && items.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-500" data-testid="muslin-inventory-loading-state">Loading muslin inventory…</div>
            ) : displayedItems.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 py-12 text-center text-sm text-slate-500" data-testid="muslin-inventory-empty-state">
                <Package2 className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                {emptyStateMessage}
              </div>
            ) : (
              <div className="relative">
              {isFetching && (
                <div className="mb-2 text-xs text-slate-500" data-testid="muslin-inventory-inline-loading-text">
                  Updating the table while keeping your current rows visible…
                </div>
              )}
              <div className="max-h-[calc(100vh-255px)] overflow-auto rounded-xl border border-slate-200" data-testid="muslin-inventory-table-scroll-region">
              <Table data-testid="muslin-inventory-table" className={isFetching ? 'opacity-75' : ''}>
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                    <TableHead className="w-[44px]">
                      <Checkbox
                        checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                        onCheckedChange={handleSelectAllRows}
                        aria-label="Select all visible muslin items"
                        data-testid="muslin-inventory-select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Box / Store</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pattern / Source</TableHead>
                    <TableHead>Reservation</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedItems.map((item) => (
                    <TableRow key={item.muslin_inventory_id} data-testid={`muslin-inventory-row-${item.muslin_inventory_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedItemIds.includes(item.muslin_inventory_id)}
                          onCheckedChange={(checked) => handleSelectRow(item.muslin_inventory_id, checked)}
                          aria-label={`Select muslin record ${item.reference_code}`}
                          data-testid={`muslin-inventory-row-select-${item.muslin_inventory_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-slate-900">{item.reference_code}</div>
                      </TableCell>
                      <TableCell className="capitalize">{item.garment_type || '—'}</TableCell>
                      <TableCell>
                        <div>{normalizeBoxDisplay(item.box_code)}</div>
                        <div className="text-xs text-slate-500">{item.store || '—'}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusTone[item.status] || statusTone.available} data-testid={`muslin-inventory-status-${item.muslin_inventory_id}`}>
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span>{item.linked_pattern_code || 'No linked pattern'}</span>
                          {item.fitting_studio_customer_id && item.garment_type && (
                            <Button
                              asChild
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-500 hover:text-slate-900"
                              data-testid={`muslin-inventory-open-fitting-studio-${item.muslin_inventory_id}`}
                            >
                              <a
                                href={`/fitting-studio/${item.fitting_studio_customer_id}?tab=${item.garment_type}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`Open fitting studio for ${item.linked_pattern_code || item.reference_code}`}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          )}
                        </div>
                        {showSnapshotDetails && (
                          <>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <Badge
                                className={measurementTone[item.measurement_coverage_status] || measurementTone.no_pattern}
                                data-testid={`muslin-inventory-measurement-status-${item.muslin_inventory_id}`}
                              >
                                {measurementLabel[item.measurement_coverage_status] || measurementLabel.no_pattern}
                              </Badge>
                              <span className="text-[11px] text-slate-500" data-testid={`muslin-inventory-measurement-count-${item.muslin_inventory_id}`}>
                                {item.has_measurement_snapshot
                                  ? `${item.measurement_field_count_present || 0}/${item.measurement_field_count_total || 0} fields${item.design_field_count_total ? ` • Design ${item.design_field_count_present || 0}/${item.design_field_count_total}` : ''}`
                                  : 'No muslin snapshot yet'}
                              </span>
                            </div>
                            {item.coverage_detail && (
                              <div className="mt-1 text-[11px] text-rose-700" data-testid={`muslin-inventory-coverage-detail-${item.muslin_inventory_id}`}>
                                {item.coverage_detail}
                              </div>
                            )}
                            {item.snapshot_warning_note && (
                              <div className="mt-1 text-[11px] text-amber-700" data-testid={`muslin-inventory-snapshot-warning-${item.muslin_inventory_id}`}>
                                {item.snapshot_warning_note}
                              </div>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{item.reserved_for_order_number || '—'}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {item.status === 'reserved' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRelease(item)}
                              data-testid={`muslin-inventory-release-button-${item.muslin_inventory_id}`}
                            >
                              <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Release
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setEditingItem(item); setDialogOpen(true); }}
                            data-testid={`muslin-inventory-edit-button-${item.muslin_inventory_id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (isDeleteBlocked(item)) {
                                toast.error('Release this muslin record before deleting it');
                                return;
                              }
                              setDeleteTarget(item);
                              setDeleteDialogOpen(true);
                            }}
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            data-testid={`muslin-inventory-delete-button-${item.muslin_inventory_id}`}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              </div>
            )}
            <div className="mt-4 text-xs text-slate-500" data-testid="muslin-inventory-delete-help-text">
              Bulk delete only works for unreserved muslin records. Reserved or linked records must be released first.
            </div>
          </CardContent>
        </Card>
      </div>

      <MuslinInventoryFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingItem(null);
        }}
        onSubmit={handleSubmitItem}
        stores={stores}
        initialValues={editingItem || { store: '', status: 'available' }}
        title={editingItem ? 'Edit Muslin Inventory Item' : 'Add Muslin Inventory Item'}
        description="Update inventory details and maintain the muslin snapshot used for matching and readiness."
        submitLabel={editingItem ? 'Save Changes' : 'Save Item'}
        saving={dialogSaving}
      />

      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-h-[88vh] max-w-5xl overflow-hidden p-0" data-testid="muslin-import-dialog">
          <div className="flex max-h-[88vh] flex-col overflow-hidden">
            <DialogHeader className="border-b border-slate-200 px-6 py-4">
              <DialogTitle className="flex items-center gap-2 text-lg" data-testid="muslin-import-dialog-title">
                <Sparkles className="h-4 w-4 text-amber-600" /> Import from Google Sheet
              </DialogTitle>
              <DialogDescription className="text-xs" data-testid="muslin-import-dialog-description">
                Paste the sheet URL, optionally choose a tab, then preview the rows before importing them into a selected store.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Google Sheet URL</div>
                  <Input
                    value={sheetUrl}
                    onChange={(event) => setSheetUrl(event.target.value)}
                    placeholder="Paste the muslin Google Sheet URL"
                    data-testid="muslin-import-sheet-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Tab Name</div>
                  <Input
                    value={sheetTabName}
                    onChange={(event) => setSheetTabName(event.target.value)}
                    placeholder="Optional tab name"
                    data-testid="muslin-import-tab-name-input"
                  />
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-[220px_auto] lg:items-end">
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Import Store</div>
                  <Select value={importStore} onValueChange={setImportStore}>
                    <SelectTrigger className="w-full" data-testid="muslin-import-store-trigger"><SelectValue placeholder="Select import store" /></SelectTrigger>
                    <SelectContent data-testid="muslin-import-store-content">
                      {stores.map((store) => <SelectItem key={store} value={store}>{store}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handlePreviewImport} disabled={previewLoading || !importStore} data-testid="muslin-import-preview-button">
                    {previewLoading ? 'Previewing...' : 'Preview Import'}
                  </Button>
                  <Button onClick={handleApplyImport} disabled={applyingImport || selectedCells.length === 0 || !importStore || !importPreview} data-testid="muslin-import-apply-button">
                    {applyingImport ? 'Importing...' : `Import ${selectedCells.length} selected`}
                  </Button>
                  <Button variant="outline" onClick={handleResetImport} data-testid="muslin-import-reset-button">
                    Reset
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-[11px] text-slate-500" data-testid="muslin-import-store-help-text">
                A store assignment is required before previewing and importing muslin rows.
              </div>

              {importPreview && (
                <Card className="border-slate-200 bg-white/95 shadow-sm" data-testid="muslin-import-preview-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Import Preview • {importPreview.selected_tab_name}</CardTitle>
                    <CardDescription className="text-xs">
                      {importPreview.total_items} entries detected. Imported items will be assigned to {importStore}.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid="muslin-import-preview-list">
                      {(importPreview.items || []).slice(0, 24).map((item) => (
                        <label key={item.source_cell} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-2.5">
                          <Checkbox
                            checked={selectedCells.includes(item.source_cell)}
                            onCheckedChange={(checked) => {
                              setSelectedCells((prev) => checked
                                ? Array.from(new Set([...prev, item.source_cell]))
                                : prev.filter((cell) => cell !== item.source_cell));
                            }}
                            data-testid={`muslin-import-select-${item.source_cell}`}
                          />
                          <div className="space-y-1 text-[11px] text-slate-700">
                            <div className="font-semibold text-slate-900">{item.reference_code}</div>
                            <div>{item.box_code} • {item.source_cell}</div>
                            <div>{item.garment_type || 'Type unknown'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MuslinDesignBackfillDialog
        open={designBackfillDialogOpen}
        onOpenChange={setDesignBackfillDialogOpen}
        loading={designBackfillLoading}
        applying={designBackfillApplying}
        previewData={designBackfillPreview}
        scopeLabel={designBackfillScopeLabel}
        onApply={handleApplyDesignBackfill}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="muslin-inventory-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete muslin record?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-semibold text-slate-900">{deleteTarget?.reference_code || 'this record'}</span>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="muslin-inventory-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSingle}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="muslin-inventory-delete-confirm-button"
            >
              {deleting ? 'Deleting...' : 'Delete Record'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent data-testid="muslin-inventory-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected muslin records?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete {selectedVisibleCount} selected muslin record{selectedVisibleCount === 1 ? '' : 's'}. Reserved or linked records are excluded from bulk delete and must be released first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="muslin-inventory-bulk-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={deleting || selectedVisibleCount === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="muslin-inventory-bulk-delete-confirm-button"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedVisibleCount} Record${selectedVisibleCount === 1 ? '' : 's'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
